if (!customElements.get('cookie-banner')) {
  customElements.define(
    'cookie-banner',
    class CookieBanner extends ModalElement {
      constructor() {
        super();
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get acceptButton() {
        return this.querySelector('button[name="accept"]');
      }
    
      get declineButton() {
        return this.querySelector('button[name="decline"]');
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;
    
        if (this.testMode) {
          this.load(this.delay);
          return;
        }
    
        window.Shopify.loadFeatures([{
          name: 'consent-tracking-api',
          version: '0.1',
          onLoad: this.onConsentLibraryLoaded.bind(this)
        }]);
      }
    
      load(delay) {
        if (Shopify.designMode) return;
    
        setTimeout(() => this.show(), delay * 1000);
    
        if (this.acceptButton) this.acceptButton.addEventListener('click', this.acceptPolicy.bind(this));
        if (this.declineButton) this.declineButton.addEventListener('click', this.declinePolicy.bind(this));
        this.querySelector('button[name="customize"]')?.addEventListener('click', this.customize.bind(this));
      }

      // Customize: Shopify's preferences window takes over.
      // Desktop: the banner goes at once and the window opens as usual.
      // Phones: the banner becomes the window. The card stays on screen until
      // Shopify has built its window (no blank frame), then in one frame the
      // window takes the card's exact shape and glass, and grows into the
      // sheet: Decline and Accept travel to their new places while their
      // labels cross-fade, the banner's words fade up and away, and the
      // window's own content settles in through a soft blur.
      customize() {
        if (typeof window.privacyBanner?.showPreferences !== 'function') return;

        const inner = this.querySelector('.drawer__inner');
        const morph = window.matchMedia('(max-width: 639px)').matches
          && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
          && typeof inner?.animate === 'function';

        if (!morph) {
          this.hide();
          this.vanish(inner);
          window.privacyBanner.showPreferences();
          return;
        }

        const from = {
          card: inner.getBoundingClientRect(),
          decline: this.declineButton?.getBoundingClientRect(),
          accept: this.acceptButton?.getBoundingClientRect()
        };
        document.documentElement.classList.add('pc-morph-pending');
        window.privacyBanner.showPreferences();
        this.waitForPreferences(inner, from);
      }

      // hides the card immediately, cutting any closing fade short
      vanish(inner) {
        if (!inner) return;
        inner.style.setProperty('transition', 'none', 'important');
        inner.style.setProperty('opacity', '0', 'important');
        inner.getAnimations().forEach((animation) => animation.cancel());
        setTimeout(() => { inner.style.removeProperty('transition'); inner.style.removeProperty('opacity'); }, 900);
      }

      waitForPreferences(inner, from) {
        const started = performance.now();
        const check = () => {
          const prefs = document.getElementById('shopify-pc__prefs');
          const dialog = document.getElementById('shopify-pc__prefs__dialog');
          if (prefs && dialog && getComputedStyle(prefs).display !== 'none' && dialog.getBoundingClientRect().height > 0) {
            this.morphIntoPreferences(inner, prefs, dialog, from);
          } else if (performance.now() - started < 1500) {
            requestAnimationFrame(check);
          } else {
            document.documentElement.classList.remove('pc-morph-pending');
            this.hide();
            this.vanish(inner);
          }
        };
        check();
      }

      // a copy of part of the card, pinned where it was, so it can fade on top
      ghost(element) {
        const rect = element.getBoundingClientRect();
        const copy = element.cloneNode(true);
        copy.removeAttribute('id');
        copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        // custom elements (the title's word-by-word reveal) would start their
        // own hidden entrance again when copied: the copy keeps the words only
        if (element.querySelector('*') && [...element.querySelectorAll('*')].some((node) => node.localName.includes('-'))) {
          copy.textContent = element.textContent.replace(/\s+/g, ' ').trim();
        }
        // the copy leaves the banner's styles behind, so it carries its look inline
        const props = ['color', 'background-color', 'background-image', 'box-shadow', 'border-radius', 'font-family', 'font-size', 'font-weight', 'line-height', 'letter-spacing', 'text-align', 'display', 'align-items', 'justify-content', 'gap', 'padding', 'margin', 'white-space', 'text-decoration', 'opacity', 'transform', 'visibility'];
        const originals = copy.children.length ? [element, ...element.querySelectorAll('*')] : [element];
        [copy, ...copy.querySelectorAll('*')].forEach((node, index) => {
          const computed = getComputedStyle(originals[index]);
          props.forEach((prop) => node.style.setProperty(prop, computed.getPropertyValue(prop)));
          node.style.setProperty('transition', 'none');
          node.style.setProperty('animation', 'none');
        });
        Object.assign(copy.style, {
          position: 'fixed', left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`,
          margin: '0', zIndex: '2000005', pointerEvents: 'none', boxSizing: 'border-box'
        });
        copy.setAttribute('aria-hidden', 'true');
        document.body.append(copy);
        return copy;
      }

      // the card button's label alone, centred where it was
      ghostLabel(button) {
        const rect = button.getBoundingClientRect();
        const style = getComputedStyle(button);
        const label = document.createElement('span');
        label.textContent = button.textContent.replace(/\s+/g, ' ').trim();
        label.setAttribute('aria-hidden', 'true');
        Object.assign(label.style, {
          position: 'fixed', left: `${rect.left + rect.width / 2}px`, top: `${rect.top + rect.height / 2}px`,
          transform: 'translate(-50%, -50%)', zIndex: '2000005', pointerEvents: 'none', whiteSpace: 'nowrap',
          color: style.color, fontFamily: style.fontFamily, fontSize: style.fontSize, fontWeight: style.fontWeight, letterSpacing: style.letterSpacing
        });
        document.body.append(label);
        return label;
      }

      morphIntoPreferences(inner, prefs, dialog, from) {
        const duration = 620;
        const sheet = 'cubic-bezier(0.32, 0.72, 0, 1)';      // the curve iOS sheets use
        const settle = 'cubic-bezier(0.23, 1, 0.32, 1)';
        const px = (value) => `${Math.max(0, Math.round(value))}px`;
        const animations = [];

        // where a block's text actually sits, and how big it is
        const textOf = (element) => {
          const range = document.createRange();
          range.selectNodeContents(element);
          return { box: range.getBoundingClientRect(), rect: element.getBoundingClientRect(), size: parseFloat(getComputedStyle(element).fontSize) || 16 };
        };

        // the banner's words and buttons, copied on top, so they can fade
        // while the real card is already gone
        const sources = [...inner.querySelectorAll('.cookie-banner > .heading, .cookie-banner > .rte')];
        const before = sources.map(textOf);
        const words = sources.map((node) => this.ghost(node));
        const labels = [this.declineButton, this.acceptButton].map((button) => button && this.ghostLabel(button));

        // same frame: the card goes, the window appears in the card's shape
        prefs.classList.add('is-morphing');
        this.hide();
        this.vanish(inner);
        document.documentElement.classList.remove('pc-morph-pending');

        const to = dialog.getBoundingClientRect();
        animations.push(dialog.animate([
          {
            clipPath: `inset(${px(from.card.top - to.top)} ${px(to.right - from.card.right)} ${px(to.bottom - from.card.bottom)} ${px(from.card.left - to.left)} round 14px)`,
            backgroundColor: 'rgba(255, 255, 255, 0.86)'
          },
          { clipPath: 'inset(0px 0px 0px 0px round 22px 22px 0px 0px)', backgroundColor: 'rgb(255, 255, 255)' }
        ], { duration, easing: sheet }));
        // the glass turns solid early, so the page never shows through the growing sheet
        animations.push(dialog.animate([
          { backgroundColor: 'rgba(255, 255, 255, 0.86)' },
          { backgroundColor: 'rgb(255, 255, 255)' }
        ], { duration: 260, easing: 'ease-out', composite: 'replace' }));

        const overlay = document.getElementById('shopify-pc__prefs__overlay');
        if (overlay) animations.push(overlay.animate([{ opacity: 0 }, { opacity: 1 }], { duration, easing: 'ease-out' }));

        // "We use cookies" becomes the window's title and the banner's text
        // becomes its intro: each pair rides up with the sheet's top edge from
        // the card to its place, growing to its new size, and cross-fades on
        // the way, so the words read as one piece of text changing
        const targets = [
          document.getElementById('shopify-pc__prefs__header-title'),
          dialog.querySelector('.shopify-pc__prefs__intro-main p')
        ];
        words.forEach((node, index) => {
          const start = before[index];
          const target = targets[index];
          const end = target && target.getClientRects().length ? textOf(target) : null;
          if (!end || !end.box.width) {
            animations.push(node.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 130, easing: 'ease-out', fill: 'forwards' }));
            return;
          }
          const dx = end.box.left - start.box.left;
          const dy = end.box.top - start.box.top;
          const grow = end.size / start.size;
          node.style.transformOrigin = `${start.box.left - start.rect.left}px ${start.box.top - start.rect.top}px`;
          target.style.transformOrigin = `${end.box.left - end.rect.left}px ${end.box.top - end.rect.top}px`;
          animations.push(node.animate([
            { transform: 'none' },
            { transform: `translate(${dx}px, ${dy}px) scale(${grow})` }
          ], { duration, easing: sheet, fill: 'forwards' }));
          animations.push(node.animate([
            { opacity: 1, filter: 'blur(0px)' },
            { opacity: 0, filter: 'blur(2px)' }
          ], { duration: 130, easing: 'ease-out', fill: 'forwards' }));
          animations.push(target.animate([
            { transform: `translate(${-dx}px, ${-dy}px) scale(${1 / grow})` },
            { transform: 'none' }
          ], { duration, easing: sheet }));
          animations.push(target.animate([
            { opacity: 0, filter: 'blur(2px)' },
            { opacity: 1, filter: 'blur(0px)' }
          ], { duration: 220, delay: 80, easing: 'ease-out', fill: 'backwards' }));
        });

        // Decline and Accept: each of the window's buttons starts as the card's
        // button and grows into its own. The pill itself changes size (its
        // outline is clipped, so the corners stay round and nothing stretches),
        // it slides to its place, and its colour follows. The card's label rides
        // along and fades as the new label fades in, both at their natural size.
        [['decline', 'shopify-pc__prefs__header-decline', 0], ['accept', 'shopify-pc__prefs__header-accept', 1]].forEach(([key, id, index]) => {
          const button = document.getElementById(id);
          const start = from[key];
          const label = labels[index];
          if (!button || !start) return;
          const end = button.getBoundingClientRect();
          if (!end.width || !end.height) return;
          const style = getComputedStyle(button);
          const dx = (start.left + start.width / 2) - (end.left + end.width / 2);
          const dy = (start.top + start.height / 2) - (end.top + end.height / 2);
          const insetX = Math.max(0, (end.width - start.width) / 2);
          const insetY = Math.max(0, (end.height - start.height) / 2);
          const first = {
            transform: `translate(${dx}px, ${dy}px)`,
            clipPath: `inset(${insetY}px ${insetX}px round 999px)`
          };
          const last = { transform: 'none', clipPath: 'inset(0px 0px round 999px)' };
          if (key === 'decline') {
            first.backgroundColor = '#fff';
            first.boxShadow = 'inset 0 0 0 1px rgba(0, 0, 0, 0.12)';
            last.backgroundColor = style.backgroundColor;
            last.boxShadow = style.boxShadow === 'none' ? 'inset 0 0 0 1px rgba(0, 0, 0, 0)' : style.boxShadow;
          }
          animations.push(button.animate([first, last], { duration, easing: sheet }));
          // the new label comes in once the pill has room for it
          animations.push(button.animate([
            { color: 'transparent' },
            { color: 'transparent', offset: 0.42 },
            { color: style.color }
          ], { duration: 420, easing: 'ease-out' }));
          if (label) {
            animations.push(label.animate([
              { transform: 'translate(-50%, -50%)', opacity: 1 },
              { transform: `translate(calc(-50% - ${dx}px), calc(-50% - ${dy}px))`, opacity: 0 }
            ], { duration, easing: sheet, fill: 'forwards' }));
            animations.push(label.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease-out', fill: 'forwards' }));
          }
        });

        // the close button and the cookie choices ride up with the sheet's top
        // edge, so nothing is uncovered in place under the moving title (the
        // list slides out from behind the buttons), and fade in on the way
        const rise = Math.max(0, from.card.top - to.top);
        [
          '#shopify-pc__prefs__header-close',
          '#shopify-pc__prefs__dialog .shopify-pc__prefs__options'
        ].forEach((selector, index) => {
          const element = document.querySelector(selector);
          if (!element) return;
          animations.push(element.animate([
            { transform: `translateY(${rise}px)` },
            { transform: 'none' }
          ], { duration, easing: sheet }));
          animations.push(element.animate([
            { opacity: 0, filter: 'blur(3px)' },
            { opacity: 1, filter: 'blur(0px)' }
          ], { duration: 220, delay: 60 + index * 25, easing: 'ease-out', fill: 'backwards' }));
        });
        const save = document.getElementById('shopify-pc__prefs__header-save');
        if (save) {
          animations.push(save.animate([
            { opacity: 0, transform: 'translateY(10px)', filter: 'blur(4px)' },
            { opacity: 1, transform: 'none', filter: 'blur(0px)' }
          ], { duration: 260, delay: 120, easing: settle, fill: 'backwards' }));
        }

        // once settled the window must not play its own entrance on top (that
        // read as the panel reloading): it stays "morphed" until it closes
        prefs.classList.add('is-morphed');
        Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
          prefs.classList.remove('is-morphing');
          targets.forEach((target) => target?.style.removeProperty('transform-origin'));
          [...words, ...labels].forEach((node) => node?.remove());
        });
        const watch = new MutationObserver(() => {
          if (getComputedStyle(prefs).display !== 'none') return;
          prefs.classList.remove('is-morphed');
          watch.disconnect();
        });
        watch.observe(prefs, { attributes: true, attributeFilter: ['style', 'class'] });
      }

      acceptPolicy() {
        this.hide();
        window.Shopify.customerPrivacy?.setTrackingConsent(true, this.noop);
    
      }
    
      declinePolicy() {
        this.hide();
        window.Shopify.customerPrivacy?.setTrackingConsent(false, this.noop);
      }
    
      onConsentLibraryLoaded(error) {
        const privacy = window.Shopify.customerPrivacy;
        if (error || typeof privacy?.userCanBeTracked !== 'function' || typeof privacy?.getTrackingConsent !== 'function') return;
        const userCanBeTracked = privacy.userCanBeTracked();
        const userTrackingConsent = privacy.getTrackingConsent();
    
        if(!userCanBeTracked && userTrackingConsent === 'no_interaction') {
          this.load(this.delay);
        }
      }
    
      noop() {
      }
    
      afterHide() {
        document.body.classList.remove('has-cookie-banner');
      }
      afterShow() {
        document.body.classList.add('has-cookie-banner');
      }
    }
  );
}
